import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import Table from 'Components/UI/Table';
import BreadCrumb from 'Components/UI/BreadCrumb';
import TableActionTopbar from 'Components/UI/TableActionTopbar';
import { ConnectedProps, connect, useDispatch, useSelector } from 'react-redux';
import { ListagemArquivosActions } from 'Store/ListagemArquivos/ListagemArquivos.actions';
import { PlotagemActions } from 'Store/Plotagem/Plotagem.actions';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import ReenvioSolicitacao from '../Modals/ReenvioSolicitacao/index';
import ExcluiArquivos from '../Modals/ExcluiArquivo/index';
import {
  getButtonsTableToolbar,
  breadCrumb,
  columnsTable,
  getArquivosRelatorio,
  TipoRelatorio,
} from './PlotagemArquivos.helpers';
import { IArquivoData } from 'Data/interfaces/Plotagem/IArquivoData';
import { IDownloadRelatorioRequest } from 'Data/interfaces/Plotagem/IDownloadRelatorioRequest';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import LoadingArchivesPlotagens from 'Components/UI/CustomLoading/LoadingArchivesPlotagens';
import { DisciplineIconsEnum } from 'Utils/ObraUtils';
import { ObraActions } from 'Store/Obra/Obra.actions';
import { Http } from 'Utils/Http';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import { useTimeout } from 'Context/TimeoutContext';
import styles from './PlotagemArquivos.module.scss';

const PlotagemArquivos: FC<Props> = (props) => {
  const { t } = useTranslation('plotagens');
  const dispatch = useDispatch();
  const { set, clear } = useTimeout();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);

  const [selectedArquivos, setSelectedArquivos] = React.useState([]);
  const onChange = (selecteds: any) => setSelectedArquivos(selecteds);
  const folderName = Http.GetQueryParams('folderName');
  let obraName: string | undefined;
  let disciplineName: string | undefined;
  let currentArquivos: IArquivoData[] = [];

  const rowSelection = {
    selectedArquivos,
    onChange,
  };

  const exportRelatorioPlotagem = (tipoRelatorio: TipoRelatorio) => {
    const arquivosRelatorio = getArquivosRelatorio(currentArquivos);
    clear('PlotagemArquivos-exportRelatorioPlotagem');
    set(
      'PlotagemArquivos-exportRelatorioPlotagem',
      () => {
        const request: IDownloadRelatorioRequest = {
          ArquivosRelatorio: arquivosRelatorio,
          ConstructionSiteDisciplineId: Number(params.ConstructionSiteDisciplineFk),
          constructionSiteIdInfoTracking: Number(params.constructionSiteId),
          userInfoTracking: props.userInfo,
          currentTenantTracking: currentTenant,
          tenantIds:
            tenants && tenants.length > 0
              ? (tenants
                  ?.map((tenant) => tenant?.TenantId)
                  .filter((tenantId) => tenantId !== undefined) as number[])
              : undefined,
        };
        dispatch(PlotagemActions.downloadRelatorioExcel(request));
      },
      500,
    );
  };

  const isLoading = () => {
    if (props.isLoading && !props.isDownloading) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    return () => {
      clear('PlotagemArquivos-exportRelatorioPlotagem');
    };
  }, []);

  useEffect(() => {
    if (!props.isLoading) {
      setSelectedArquivos([]);
    }
  }, [props.isLoading]);

  useEffect(() => {
    dispatch(PlotagemActions.listPlotagemArquivos(Number(params.ConstructionSiteDisciplineFk)));
    dispatch(ListagemArquivosActions.setShowPlotRequestTable(false));
  }, [params, dispatch]);

  useEffect(() => {
    const pathname = location?.pathname || '';
    const search = location?.search;
    dispatch(ObraActions.storePlotagensPath({
      path: pathname,
      params,
      url: `${pathname}${search}`,
    }));
  }, [location, params, dispatch]);

  if (props.ArquivoResponse.info && !isLoading()) {
    obraName = props.ArquivoResponse.info.PreviousItem?.Name;
    disciplineName = props.ArquivoResponse.info.Name;

    breadCrumb[0] = {
      description: `${obraName}`,
      href: `/obras/filelist/${params.constructionSiteId}`,
    };
    breadCrumb[1] = {
      description: t('breadcrumb.plotagens'),
      href: `/obras/plotagens/${params.constructionSiteId}`,
    };
    breadCrumb[2] = {
      description: `${disciplineName}`,
      href: `/obras/plotagens/filelist/${props.ArquivoResponse.info.PreviousItem?.Identifier}/${props.ArquivoResponse.info.Identifier}${folderName ? `?folderName=${folderName}` : ''}`,
    };
  } else {
    breadCrumb[0] = {
      href: '',
      isLoading: true,
    };
    breadCrumb[1] = {
      href: '',
      isLoading: true,
    };
    breadCrumb[2] = {
      href: '',
      isLoading: true,
    };
  }

  const iconLeft = () => {
    if (disciplineName && (DisciplineIconsEnum as any)[folderName || disciplineName]) {
      return (DisciplineIconsEnum as any)[folderName || disciplineName];
    }
    return 'menuContexto';
  };

  return (
    <>
      <TabsShell
        ConstructionSiteId={params.constructionSiteId}
        active={PathActiveEnum.plotagens}
        loading={isLoading()}
      />
      <PageHeader name={folderName || `${disciplineName || ''}`} iconLeft={iconLeft()} >
        <BreadCrumb breadcrumbs={breadCrumb} />
        <div className={`${isLoading() ? styles['tableLoading'] : styles['tableContainer']} ${props.isCollapsed ? styles['tableCollapsed'] : ''}`}>
          <TableActionTopbar
            buttons={isLoading() ? [] : getButtonsTableToolbar(
              selectedArquivos,
              props.ArquivoResponse.data,
              dispatch,
              navigate,
              Number(params.ConstructionSiteDisciplineFk),
              Number(params.constructionSiteId),
              exportRelatorioPlotagem,
              props.isDownloading,
              folderName,
              props.userInfo,
              currentTenant,
              tenants && tenants.length > 0
                ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
                : undefined,
            )}
          />
          {isLoading() ? (
            <LoadingArchivesPlotagens multiple={11} />
          ) : (
            <>
              <Table className={styles['tableListaArquivos']}
                footer={(currentPageData) => {
                  currentArquivos = currentPageData as any;
                  return null;
                }}
                columns={columnsTable(props.ArquivoResponse.data, props.filters, props.ArquivoResponse.info,
                  dispatch, props.ArquivoPrint)}
                dataSource={props.ArquivoResponse?.data?.map((discipline: any, index: number) =>
                  ({ ...discipline, key: index }))}
                rowSelection={rowSelection}
              />
            </>
          )}
        </div>

        {props.showModalReenviaSolicitacaoPlotagem && (
          <ReenvioSolicitacao
            visible={props.showModalReenviaSolicitacaoPlotagem}
            onCancel={() => dispatch(PlotagemActions.setShowModalReenviaSolicitacaoPlotagem(false))}
            constructionSiteId={Number(params.constructionSiteId)}
            props={props}
          />
        )}

        <ExcluiArquivos
          visible={props.showModalExcluiArquivosPlotagem}
          onCancel={() => dispatch(PlotagemActions.setShowModalExcluiArquivosPlotagem(false))}
          listaArquivos={props.ArquivoResponse.data}
          posicaoSelecionados={selectedArquivos}
          constructionSiteId={Number(params.constructionSiteId)}
          constructionSiteDisciplineFk={Number(params.ConstructionSiteDisciplineFk)}
        >
        </ExcluiArquivos>
      </PageHeader>
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.plotagem,
  isCollapsed: state.obra.isCollapsed,
  userInfo: state.auth.userInfo,
});

const connector = connect(
  mapState,
);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(PlotagemArquivos);
